/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type { ApiErrorDto, PostShareLinkDto, SharedPostDto } from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary A link (for a QR code) to the post's captions and files; replaces any older one.
 */
export const createPostShare = (id: string, postId: string, signal?: AbortSignal) => {
  return customInstance<PostShareLinkDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}/share`,
    method: 'POST',
    signal,
  });
};

export const getCreatePostShareMutationKey = () => ['createPostShare'] as const;

export const getCreatePostShareMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPostShare>>,
    TError,
    CreatePostShareMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPostShare>>,
  TError,
  CreatePostShareMutationVariables,
  TContext
> => {
  const mutationKey = getCreatePostShareMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPostShare>>,
    CreatePostShareMutationVariables
  > = (props) => {
    const { id, postId } = props ?? {};

    return createPostShare(id, postId);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePostShareMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPostShare>>
>;

export type CreatePostShareMutationError = ErrorType<ApiErrorDto>;
export type CreatePostShareMutationVariables = { id: string; postId: string };

/**
 * @summary A link (for a QR code) to the post's captions and files; replaces any older one.
 */
export const useCreatePostShare = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPostShare>>,
      TError,
      CreatePostShareMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createPostShare>>,
  TError,
  CreatePostShareMutationVariables,
  TContext
> => {
  return useMutation(getCreatePostShareMutationOptions(options), queryClient);
};
/**
 * @summary Stop the post's link working.
 */
export const revokePostShare = (id: string, postId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}/share`,
    method: 'DELETE',
    signal,
  });
};

export const getRevokePostShareMutationKey = () => ['revokePostShare'] as const;

export const getRevokePostShareMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokePostShare>>,
    TError,
    RevokePostShareMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokePostShare>>,
  TError,
  RevokePostShareMutationVariables,
  TContext
> => {
  const mutationKey = getRevokePostShareMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokePostShare>>,
    RevokePostShareMutationVariables
  > = (props) => {
    const { id, postId } = props ?? {};

    return revokePostShare(id, postId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokePostShareMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokePostShare>>
>;

export type RevokePostShareMutationError = ErrorType<ApiErrorDto>;
export type RevokePostShareMutationVariables = { id: string; postId: string };

/**
 * @summary Stop the post's link working.
 */
export const useRevokePostShare = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokePostShare>>,
      TError,
      RevokePostShareMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokePostShare>>,
  TError,
  RevokePostShareMutationVariables,
  TContext
> => {
  return useMutation(getRevokePostShareMutationOptions(options), queryClient);
};
/**
 * @summary The post's captions and files. Any dead link is the same 404.
 */
export const getSharedPost = (token: string, signal?: AbortSignal) => {
  return customInstance<SharedPostDto>({
    url: `/api/v1/share/${token}`,
    method: 'GET',
    signal,
  });
};

export const getGetSharedPostQueryKey = (token: string) => {
  return [`/api/v1/share/${token}`] as const;
};

export const getGetSharedPostQueryOptions = <
  TData = Awaited<ReturnType<typeof getSharedPost>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSharedPost>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSharedPostQueryKey(token);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSharedPost>>> = ({
    signal,
  }) => getSharedPost(token, signal);

  return {
    queryKey,
    queryFn,
    enabled: token !== null && token !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getSharedPost>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetSharedPostQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSharedPost>>
>;
export type GetSharedPostQueryError = ErrorType<ApiErrorDto>;

export function useGetSharedPost<
  TData = Awaited<ReturnType<typeof getSharedPost>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSharedPost>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSharedPost>>,
          TError,
          Awaited<ReturnType<typeof getSharedPost>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSharedPost<
  TData = Awaited<ReturnType<typeof getSharedPost>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSharedPost>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSharedPost>>,
          TError,
          Awaited<ReturnType<typeof getSharedPost>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSharedPost<
  TData = Awaited<ReturnType<typeof getSharedPost>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSharedPost>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The post's captions and files. Any dead link is the same 404.
 */

export function useGetSharedPost<
  TData = Awaited<ReturnType<typeof getSharedPost>>,
  TError = ErrorType<ApiErrorDto>,
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSharedPost>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSharedPostQueryOptions(token, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}
